import React, { useState, useEffect, useRef } from 'react'; // Aggiunti import
import { useAuth } from "react-oidc-context";
import AppLayout from './components/AppLayout/AppLayout';
import LoginFlow from './components/Login/LoginFlow';
import { cognitoAuthConfig, cognitoDomain } from './authConfig';
import config from './config';
import fotoChordA from './foto-chord-a.png';
import fotoChordA2 from './foto-chord-a-2.png';
import { Amplify } from 'aws-amplify';
import './App.css';

function App() {
  const auth = useAuth();
  const [isAmplifyLoggedIn, setIsAmplifyLoggedIn] = useState(false);
  const [showCustomLogin, setShowCustomLogin] = useState(false);
  const [amplifyUser, setAmplifyUser] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [userSelections, setUserSelections] = useState({ industry: '', job: '' });
  const fileInputRef = useRef(null);

  const signOutRedirect = () => {
    const clientId = cognitoAuthConfig.client_id;
    const logoutUri = cognitoAuthConfig.redirect_uri;
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  // Exchange a Cognito id_token for a local JWT pair via the backend OIDC endpoint.
  // Stores access_token in localStorage so AppLayout can pick it up.
  const exchangeOidcToken = async (idToken) => {
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/auth/oidc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'cognito', token: idToken }),
      });
      if (!res.ok) throw new Error(`OIDC exchange failed: ${res.status}`);
      const data = await res.json();
      localStorage.setItem('token', data.data.access_token);
      if (data.data.refresh_token) localStorage.setItem('refresh_token', data.data.refresh_token);
      return data.data.access_token;
    } catch (err) {
      console.error('Token exchange error:', err);
      return null;
    }
  };

  // 1. OIDC login (react-oidc-context / Cognito redirect flow)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.id_token) {
      exchangeOidcToken(auth.user.id_token);
    }
  }, [auth.isAuthenticated, auth.user]);

  // 2. Amplify login check
  useEffect(() => {
    import('aws-amplify/auth').then(async ({ fetchUserAttributes, fetchAuthSession }) => {
      try {
        const attributes = await fetchUserAttributes();
        console.log('Amplify user trovato:', attributes);
        setAmplifyUser(attributes);
        setIsAmplifyLoggedIn(true);
        // Exchange Amplify session token for local JWT
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) await exchangeOidcToken(idToken);
      } catch {
        console.log('Nessun utente Amplify');
        setIsAmplifyLoggedIn(false);
      }
    });
  }, []);

  const anyAuth = auth.isAuthenticated || isAmplifyLoggedIn;

  // 3. Onboarding check
  useEffect(() => {
    if (anyAuth) {
      const email = auth.user?.profile?.email || amplifyUser?.email || '';
      const token = localStorage.getItem('token') || auth.user?.id_token || '';

      console.log('Controllo onboarding per:', email);

      fetch(`${config.apiBaseUrl}/api/onboarding/check?email=${encodeURIComponent(email)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(res => {
          console.log('Onboarding status:', res);
          setNeedsOnboarding(!res.data.completed);
        })
        .catch((err) => {
          console.error('Errore controllo onboarding:', err);
          setNeedsOnboarding(false);
        });
    }
  }, [anyAuth, auth.user, amplifyUser]);

  // 4. Loading
  if (auth.isLoading || (anyAuth && needsOnboarding === null)) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    return <div>Encountering error... {auth.error.message}</div>;
  }

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 5. SE LOGGATO, mostra Dashboard o Onboarding
  if (anyAuth) {
    if (needsOnboarding) {
      console.log('Mostro onboarding per utente loggato');
      return (
        <LoginFlow
          email={auth.user?.profile?.email || amplifyUser?.email}
          auth={auth}
          isFullAuth={false}
          onSelectionChange={setUserSelections}
          onLoginComplete={() => {
            console.log('Onboarding completato');
            setNeedsOnboarding(false);
          }}
        />
      );
    }

    console.log('Mostro AppLayout');
    return (
      <AppLayout
        name={auth.user?.profile?.given_name || auth.user?.profile?.name || (auth.user?.profile?.email || amplifyUser?.email || localStorage.getItem('username') || '').split('@')[0] || undefined}
        auth={auth}
        profileImage={profileImage}
        showProfileModal={showProfileModal}
        setShowProfileModal={setShowProfileModal}
        selections={userSelections}
        handleImageChange={handleImageChange}
        fileInputRef={fileInputRef}
        signOut={() => {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('username');
          if (auth.isAuthenticated) {
            auth.removeUser().then(() => signOutRedirect());
          } else if (amplifyUser) {
            import('aws-amplify/auth').then(({ signOut }) => signOut());
          } else {
            window.location.reload();
          }
        }}
      />
    );
  }

  // 6. SE NON LOGGATO, mostra Login Custom o Landing Page
  if (showCustomLogin) {
    console.log('Mostro LoginFlow completo');
    return (
      <LoginFlow
        isFullAuth={true}
        auth={auth}
        onSelectionChange={setUserSelections}
        onLoginComplete={async () => {
          console.log('Finalizzazione login in corso...');
          setIsAmplifyLoggedIn(true);
          setShowCustomLogin(false);
          setNeedsOnboarding(null); // trigger onboarding check

          // Local backend auth already stored the JWT/username in localStorage.
          // Only enrich with Amplify data if an actual Amplify (Cognito) session exists.
          try {
            const { fetchUserAttributes, fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) {
              const attributes = await fetchUserAttributes();
              setAmplifyUser(attributes);
              await exchangeOidcToken(idToken);
            }
          } catch (err) {
            // No Amplify session (local login) — expected, not an error.
            console.log('Nessuna sessione Amplify, uso login locale');
          }
        }}
      />
    );
  }

  // Landing page for non-authenticated users
  // return (
  //   <div style={styles.landingPage}>
  //     {/* Navbar */}
  //     <nav style={styles.navbar}>
  //       <div style={styles.logo}>Chord A</div>
  //       <div style={styles.navCenter}>
  //         <a href="#usecases" style={styles.navLink}>User cases</a>
  //       </div>
  //       <div style={styles.navControls}>
  //         <button className="buttonloginhomepage" onClick={() => setShowCustomLogin(true)}>Log in</button>
  //         <button className="buttonsignuphomepage" onClick={() => setShowCustomLogin(true)}>Sign in</button>
  //         <span style={styles.langSelector}> ITA  </span>
  //       </div>
  //     </nav>


  //     {/* Hero Section */}
  //     <header style={styles.hero}>
  //       <h1 style={styles.heroTitle}>Chord A</h1>
  //       <p style={styles.subtitle}>Local AI, global impact</p>
  //       <button style={styles.scrollIndicator} onClick={() => setShowCustomLogin(true)}>⌄</button>
  //     </header>

  //     {/* Problem Section */}
  //     <section style={styles.section}>
  //       <h2 style={styles.sectionTitle}>The unsolved problem in monitoring solution</h2>

  //       <div style={styles.problemGrid}>
  //         <div style={styles.imageContainer}>
  //           <div style={styles.imagePlaceholder}>
  //             <img
  //               src={fotoChordA}
  //               alt="Engineer pointing"
  //               style={styles.image}
  //             />
  //           </div>
  //         </div>

  //         <div style={styles.captionBox}>
  //           <h3 style={styles.captionTitle}>A slow process</h3>
  //           <p style={styles.captionText}>
  //             As a Service Manager, <span style={styles.highlight}>I need a tool that helps me quickly and accurately configure technical quotes</span> for structural monitoring systems.
  //           </p>
  //           <p style={styles.captionSubtext}>
  //             The current process is slow, manual, and prone to errors, which delays sales and risks losing deals.
  //           </p>
  //         </div>
  //       </div>

  //       <div style={styles.additionalProblems}>
  //         <div style={styles.problemCard}>
  //           <p style={styles.problemText}>
  //             As a Geotechnical Engineer, I spend weeks building quotes.
  //           </p>
  //           <p style={styles.problemText}>
  //             Every project is custom, and I keep <span style={styles.highlight}>redoing specs from scratch.</span>
  //           </p>
  //         </div>

  //         <div style={styles.problemCard}>
  //           <p style={styles.problemText}>
  //             As a CEO of sensors maker company, we have thousands of sensor products, but no way to help clients choose what fits their use case.
  //           </p>
  //           <p style={styles.problemText}>
  //             <span style={styles.highlight}>We need smart guidance.</span>
  //           </p>
  //         </div>
  //       </div>

  //       {/* Time Waste Hero */}
  //       <div style={styles.wasteTimeSection}>
  //         <div style={styles.timeIndicator}>
  //           {/*<span style={styles.timeEmoji}>⏱️</span>*/}
  //           <span style={styles.timeNumber}>60</span>
  //         </div>
  //         <h2 style={styles.bigNumber}>60-80 hours</h2>
  //         <p style={styles.wasteText}>Average quote time for Structural health monitoring systems.</p>
  //       </div>
  //     </section>

  //     {/* Solution Section */}
  //     <section style={styles.section}>
  //       <h2 style={styles.sectionTitle}>The solution: Chord A</h2>

  //       <div style={styles.solutionGrid}>
  //         <div style={styles.imageContainer}>
  //           <div style={styles.imagePlaceholder}>
  //             <img
  //               src={fotoChordA2}
  //               alt="Laptop with monitoring dashboard"
  //               style={styles.image}
  //             />
  //           </div>
  //         </div>

  //         <div style={styles.featureCard}>
  //           <p style={styles.featureText}>
  //             Chord A is a SaaS AI Agent that designs and quotes Structural Monitoring systems with tailored sensors, architectures and AI models.
  //           </p>
  //         </div>
  //       </div>

  //       {/* Characteristics */}
  //       <div style={styles.characteristicsSection}>
  //         <h2 style={styles.sectionTitle}>Characteristics and main features</h2>

  //         <div style={styles.featuresGrid}>
  //           <div style={styles.largeFeatureCard}>
  //             {/* Placeholder for main visual */}
  //             <div style={styles.featurePlaceholder}></div>
  //           </div>

  //           <div style={styles.smallFeatureCard}>
  //             <h4 style={styles.featureTitle}>Speed and efficiency</h4>
  //             <p style={styles.featureDescription}>
  //               Chord A is an AI-powered <span style={styles.highlight}>quoting platform</span>.
  //             </p>
  //             <p style={styles.featureDescription}>
  //               It <span style={styles.highlight}>automates</span> sensor evaluation, vendor matching and documentation generation, <span style={styles.highlight}>reducing quote time and costs.</span>
  //             </p>
  //             <div style={styles.featureIcon}>⌄</div>
  //           </div>
  //         </div>

  //         <div style={styles.featuresGrid}>
  //           <div style={styles.smallFeatureCard}>
  //             <h4 style={styles.featureTitle}>Keep track of the process</h4>
  //             <p style={styles.featureDescription}>
  //               Within Chord A, conversations are automatically divided into <span style={styles.highlight}>main sections</span> based on what is being discussed.
  //             </p>
  //             <p style={styles.featureDescription}>
  //               You <span style={styles.highlight}>can't get lost!</span>
  //             </p>
  //             <div style={styles.featureIcon}>⌄</div>
  //           </div>

  //           <div style={styles.largeFeatureCard}>
  //             <div style={styles.featurePlaceholder}></div>
  //           </div>
  //         </div>

  //         <div style={styles.filesFeature}>
  //           <div style={styles.smallFeatureCard}>
  //             <h4 style={styles.featureTitle}>Keep track of your files</h4>
  //             <p style={styles.featureDescription}>
  //               The Chord A interface allows you to insert your files easily and intuitively during the conversation.
  //             </p>
  //           </div>
  //           <div style={styles.largeFeatureCard}>
  //             <div style={styles.featurePlaceholder}></div>
  //           </div>
  //         </div>
  //       </div>
  //     </section>

  //     {/* Benefits Section */}
  //     <section style={styles.section}>
  //       <h2 style={styles.sectionTitle}>Benefits of using Chord A</h2>

  //       <div style={styles.benefitsGrid}>
  //         <div style={styles.benefitCard}>
  //           <h4 style={styles.benefitTitle}>Reduced time to quote</h4>
  //           <p style={styles.benefitText}>
  //             Streamlines your quoting process from two weeks to <span style={styles.highlight}>just 15 minutes</span>. You can save over 90% of technical pre-sales effort with our AI-powered configuration engine.
  //           </p>
  //         </div>

  //         <div style={styles.benefitCard}>
  //           <h4 style={styles.benefitTitle}>Enhance data privacy</h4>
  //           <p style={styles.benefitText}>
  //             Ensures your data privacy and compliance with a patent-pending system for generating and deploying AI models entirely <span style={styles.highlight}>on edge, no cloud required</span>.
  //           </p>
  //         </div>

  //         <div style={styles.benefitCard}>
  //           <h4 style={styles.benefitTitle}>Simplify integration</h4>
  //           <p style={styles.benefitText}>
  //             Reduce integration complexity by <span style={styles.highlight}>automatically matching</span> technical requirements to real-world sensors, gateways and edge components from trusted vendors.
  //           </p>
  //         </div>

  //         <div style={styles.benefitCard}>
  //           <h4 style={styles.benefitTitle}>Provide logic and pricing</h4>
  //           <p style={styles.benefitText}>
  //             Chord A generates a <span style={styles.highlight}>complete document</span> with all the necessary information, logic, justification and pricing details.
  //           </p>
  //         </div>
  //       </div>
  //     </section>

  //     {/* Footer */}
  //     <footer style={styles.footer}>
  //       <div style={styles.footerLogo}>banjo</div>
  //       <nav style={styles.footerNav}>
  //         <a href="#work" style={styles.footerLink}>Work with us</a>
  //         <a href="#cookie" style={styles.footerLink}>Cookie Policy</a>
  //         <a href="#privacy" style={styles.footerLink}>Privacy</a>
  //         <a href="#contacts" style={styles.footerLink}>Contacts</a>
  //       </nav>
  //       <div style={styles.socialIcon}>ln</div>
  //     </footer>
  //   </div>
  // );

  // Landing page for non-authenticated users
  return (
    <div style={styles.landingPage}>
      {/* Navbar */}
      <nav style={styles.navbar}>
        <div style={styles.logo}>Chord A</div>
        <div style={styles.navCenter}>
          <a href="/chord-b/" style={styles.navLink}>Chord B</a>
        </div>
        <div style={styles.navControls}>
          <button className="buttonloginhomepage" onClick={() => setShowCustomLogin(true)}>Log in</button>
          <button className="buttonsignuphomepage" onClick={() => setShowCustomLogin(true)}>Sign in</button>
          <span style={styles.langSelector}> ITA  </span>
        </div>
      </nav>


      {/* Hero Section */}
      <header style={styles.hero}>
        <h1 style={styles.heroTitle}>Chord A</h1>
        <p style={styles.subtitle}>Local AI, global impact</p>
        <button style={styles.btnPrimary} onClick={() => setShowCustomLogin(true)}>Start chatting!</button>
      </header>

      
      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerLogo}>banjo</div>
        <nav style={styles.footerNav}>
          <a href="#work" style={styles.footerLink}>Work with us</a>
          <a href="#cookie" style={styles.footerLink}>Cookie Policy</a>
          <a href="#privacy" style={styles.footerLink}>Privacy</a>
          <a href="#contacts" style={styles.footerLink}>Contacts</a>
        </nav>
        <div style={styles.socialIcon}>ln</div>
      </footer>
    </div>
  );
}

const styles = {
  landingPage: {
    backgroundColor: '#fdf7f0',
    color: '#4A3E3E',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    minHeight: '100vh',
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  navbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 50px',
  },
  logo: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#BC8F6F',
  },
  navCenter: {
    flex: 1 ,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '10px',
    marginLeft: '70px',
  },
  navLink: {
    color: '#BC8F6F',
    textDecoration: 'none',
    fontSize: '16px',
  },
  navControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  Secondary: {
    background: 'transparent',
    border: '1px solid #D9C5B2',
    padding: '8px 20px',
    borderRadius: '10px',
    cursor: 'pointer',
    color: '#4A3E3E',
    fontSize: '14px',
  },
  btnPrimary: {
    backgroundColor: '#D9C5B2',
    border: 'none',
    padding: '10px 25px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    color: '#4A3E3E',
    fontSize: '14px',
  },
  langSelector: {
    fontSize: '14px',
    marginLeft: '5px',
  },
  hero: {
    textAlign: 'center',
    padding: '20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '32px',
  },
  heroTitle: {
    fontSize: '5rem',
    color: '#BC8F6F',
    margin: '0',
    fontWeight: '400',
  },
  subtitle: {
    fontSize: '1.5rem',
    color: '#BC8F6F',
    margin: '0',
    fontWeight: '300',
  },
  scrollIndicator: {
    fontSize: '2rem',
    color: '#BC8F6F',
    marginTop: '60px',
  },
  section: {
    padding: '60px 10%',
  },
  sectionTitle: {
    fontSize: '2rem',
    color: '#4A3E3E',
    textAlign: 'center',
    marginBottom: '50px',
    fontWeight: '500',
  },
  problemGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    marginBottom: '40px',
    alignItems: 'center',
  },
  imageContainer: {
    width: '100%',
  },
  imagePlaceholder: {
    backgroundColor: '#E8DFD3',
    borderRadius: '20px',
    overflow: 'hidden',
    aspectRatio: '4/3',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  captionBox: {
    backgroundColor: '#F4ECE4',
    border: '2px solid #D9C5B2',
    borderRadius: '20px',
    padding: '30px',
    position: 'relative',
  },
  captionTitle: {
    fontSize: '1.5rem',
    color: '#4A3E3E',
    marginTop: '0',
    marginBottom: '15px',
  },
  captionText: {
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '10px',
  },
  captionSubtext: {
    fontSize: '0.95rem',
    lineHeight: '1.5',
    color: '#6B5E5E',
  },
  highlight: {
    color: '#D4A373',
    fontWeight: '500',
  },
  additionalProblems: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px',
    marginBottom: '60px',
  },
  problemCard: {
    backgroundColor: '#F4ECE4',
    border: '2px solid #D9C5B2',
    borderRadius: '20px',
    padding: '25px',
  },
  problemText: {
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '10px',
  },
  wasteTimeSection: {
    textAlign: 'center',
    padding: '40px 0',
  },
  timeIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  timeEmoji: {
    fontSize: '2rem',
  },
  timeNumber: {
    fontSize: '2rem',
    fontWeight: '600',
    color: '#D4A373',
  },
  bigNumber: {
    fontSize: '5rem',
    color: '#D4A373',
    margin: '10px 0',
    fontWeight: '400',
  },
  wasteText: {
    fontSize: '1.2rem',
    color: '#4A3E3E',
  },
  solutionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    marginBottom: '80px',
    alignItems: 'center',
  },
  featureCard: {
    backgroundColor: '#F4ECE4',
    border: '2px solid #D9C5B2',
    borderRadius: '20px',
    padding: '30px',
  },
  featureText: {
    fontSize: '1.1rem',
    lineHeight: '1.7',
  },
  characteristicsSection: {
    marginTop: '80px',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '30px',
    marginBottom: '30px',
  },
  largeFeatureCard: {
    backgroundColor: '#F4ECE4',
    border: '2px solid #D9C5B2',
    borderRadius: '20px',
    padding: '20px',
    minHeight: '300px',
  },
  smallFeatureCard: {
    backgroundColor: '#F4ECE4',
    border: '2px solid #D9C5B2',
    borderRadius: '20px',
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
  },
  featureTitle: {
    fontSize: '1.3rem',
    color: '#4A3E3E',
    marginTop: '0',
    marginBottom: '15px',
  },
  featureDescription: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    marginBottom: '10px',
  },
  featureIcon: {
    fontSize: '1.5rem',
    color: '#D4A373',
    marginTop: 'auto',
    textAlign: 'center',
  },
  featurePlaceholder: {
    backgroundColor: '#E8DFD3',
    borderRadius: '15px',
    width: '100%',
    height: '100%',
    minHeight: '250px',
  },
  filesFeature: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '30px',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '25px',
  },
  benefitCard: {
    backgroundColor: '#EFE6DA',
    border: '2px solid #D4A373',
    borderRadius: '20px',
    padding: '30px',
  },
  benefitTitle: {
    fontSize: '1.2rem',
    color: '#BC8F6F',
    marginTop: '0',
    marginBottom: '15px',
  },
  benefitText: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 50px',
    borderTop: '1px solid #D9C5B2',
  },
  footerLogo: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#BC8F6F',
  },
  footerNav: {
    display: 'flex',
    gap: '30px',
  },
  footerLink: {
    color: '#BC8F6F',
    textDecoration: 'none',
    fontSize: '14px',
  },
  socialIcon: {
    fontSize: '18px',
    color: '#BC8F6F',
  },
};
export default App;